import { PaginationInfo } from 'Angular/features/components/pagination/interfaces/pagination-info.interface';
import { FactorCoefficientRangeData } from 'ChartApp/ts/features/factor-coefficient-range/factor-coefficient-range.interface';
import { ScatterPlotInfoInteractive } from 'ChartApp/ts/interfaces/scatter-plot-service-responses.interface';

export interface ContributorsDetractors {
    change: string;
    change_percent: string;
    color: string;
    contribution_points: string;
    contribution_points_fmtd: string;
    display_security_id: string;
    security_id: string;
    security_name: string;
    url: string;
}

export interface AttributionTableHolding {
    display_security_id: string;
    security_id: string;
    security_name: string;
    weighting: string;
    one_month_contribution: number | null;
    three_month_contribution: number | null;
    six_month_contribution: number | null;
    ytd_contribution: number | null;
    one_year_contribution: number | null;
}

export interface ContributorsDetractorsGetViewResponse {
    security_id: string;
    contributors: ContributorsDetractors[];
    detractors: ContributorsDetractors[];
    most_recent_date: string;
}

export interface AttributionTableGetViewResponse {
    security_id: string;
    holdings: AttributionTableHolding[];
    pagination_info: PaginationInfo;
}

export interface RelativeAttributionCategory {
    category_name: string;
    portfolio_weight: number;
    portfolio_return: number;
    benchmark_weight: number;
    benchmark_return: number;
    excess_return: number;
    allocation: number;
    selection: number;
    interaction: number;
    total: number;
    total_css_class: string;
    excess_return_css_class: string;
    holdings: RelativeAttributionCategoryHolding[];
}

export interface RelativeAttributionCategoryHolding {
    security_id: string;
    security_url: string;
    security_weight: number;
    security_return: number;
    benchmark_weight: number;
    benchmark_return: number;
    allocation: number;
    selection: number;
    interaction: number;
    total: number;
}

export interface DisplayRelativeAttributionCategory {
    name: string;
    portfolioWeight: string | null;
    portfolioReturn: string | null;
    benchmarkWeight: string | null;
    benchmarkReturn: string | null;
    allocation: string | null;
    selection: string | null;
    interaction: string | null;
    total: string | null;
    totalCellColorClass: string;
    holdings: DisplayRelativeAttributionCategoryHolding[];
    expanded: boolean;
}

export interface DisplayRelativeAttributionCategoryHolding {
    securityId: string;
    securityUrl: string;
    securityWeight: string | null;
    securityReturn: string | null;
    benchmarkWeight: string | null;
    benchmarkReturn: string | null;
    allocation: string | null;
    selection: string | null;
    interaction: string | null;
    total: string | null;
}

export interface DisplayAttributionPerformanceCategory {
    name: string;
    portfolioReturn: string | null;
    benchmarkReturn: string | null;
    excessReturn: string | null;
    excessReturnCellCssClass: string;
}

export interface RelativeAttributionGetViewResponse {
    data: RelativeAttributionCategory[];
}

export type FactorKey = 'growth' | 'value' | 'momentum' | 'size' | 'quality' | 'volatility' | 'dividend' | 'intercept';

export interface FactorAttributionValue {
    label: string;
    value: number;
    css_class: string;
    coefficient: number;
}

export type FactorAttributionData = Record<FactorKey, FactorAttributionValue>;

export interface FactorAttributionGetViewResponse {
    data: FactorAttributionData;
    scatterplot: ScatterPlotInfoInteractive;
    factor_profile: FactorCoefficientRangeData[];
    category_group_name: string;
}

export enum AttributionBenchmarkOption {
    ASSIGNED = 'assigned',
    S_AND_P = 's_and_p',
    CUSTOM = 'custom',
}

export enum AttributionCategorization {
    SECTOR = 'sector',
    EQUITY_STYLE = 'equity_style',
    BOND_SECTOR = 'bond_sector',
    BOND_MATURITY = 'bond_maturity',
    BROAD_ASSET_CLASS = 'broad_asset_class',
}

export enum PerformancePeriodicity {
    ONE_DAY = '1d',
    FIVE_DAYS = '5d',
    ONE_MONTH = '1m',
    THREE_MONTHS = '3m',
    SIX_MONTHS = '6m',
    YTD = 'ytd',
    ONE_YEAR = '1y',
    THREE_YEARS = '3y',
    FIVE_YEARS = '5y',
    TEN_YEARS = '10y',
    TWENTY_YEARS = '20y',
    MAX = 'max',
}

export interface PeriodicityOption {
    label: string;
    value: PerformancePeriodicity;
    disabled: boolean;
}
